'use client';

import { useState, useEffect } from 'react';
import type React from 'react';
import { createClient } from '@/lib/supabase/client';

interface SplashScreenProps {
  onEnter?: () => void;
}

export default function SplashScreen({ onEnter }: SplashScreenProps) {
  const [mounted, setMounted] = useState(false);
  const [particles, setParticles] = useState<Array<{ id: number; x: number; size: number; delay: number; duration: number }>>([]);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const validateEmail = (value: string): string => {
    if (!value.trim()) return 'Please enter your email address.';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return 'Please enter a valid email address.';
    return '';
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (emailError) setEmailError(validateEmail(value));
    if (submitError) setSubmitError('');
  };

  const handleSubmit = async () => {
    const error = validateEmail(email);
    if (error) {
      setEmailError(error);
      return;
    }
    setSubmitting(true);
    setSubmitError('');

    try {
      const supabase = createClient();
      const { error: dbError } = await supabase
        .from('email_registrations')
        .insert({ email: email.trim().toLowerCase(), source: 'coming_soon' });

      if (dbError) {
        if (dbError.code === '23505') {
          setSubmitted(true);
        } else {
          setSubmitError('Something went wrong. Please try again.');
        }
      } else {
        setSubmitted(true);
      }
    } catch {
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
      setEmail('');
      setEmailError('');
    }
  };

  useEffect(() => {
    setMounted(true);
    const pts = Array.from({ length: 12 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      size: Math.random() * 3 + 1,
      delay: Math.random() * 8,
      duration: Math.random() * 8 + 10,
    }));
    setParticles(pts);
  }, []);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #1A0E08 0%, #3C1B0D 50%, #2A1208 100%)' }}
    >
      {/* Background image with Ken Burns */}
      <div className="absolute inset-0 overflow-hidden">
        <img
          src="/assets/images/image-1785856634314.png"
          alt="Key Privé luxury property entrance with warm golden light"
          className="w-full h-full object-cover opacity-40 animate-ken-burns"
          style={{ transformOrigin: 'center center' }}
        />
      </div>

      {/* Layered overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#1A0E08]/85 via-[#3C1B0D]/35 to-[#1A0E08]/92" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#1A0E08]/65 via-transparent to-[#1A0E08]/65" />

      {/* Floating gold particles */}
      {mounted && particles.map((p) => (
        <div
          key={p.id}
          className="absolute rounded-full pointer-events-none"
          style={{
            left: `${p.x}%`,
            bottom: '-10px',
            width: `${p.size}px`,
            height: `${p.size}px`,
            background: `radial-gradient(circle, #D4A84B, #C4933A)`,
            animationName: 'particleDrift',
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
            animationTimingFunction: 'linear',
            animationIterationCount: 'infinite',
            opacity: 0,
          }}
        />
      ))}

      {/* Corner ornaments */}
      <div className="absolute top-8 left-8 w-16 h-16 pointer-events-none"
        style={{ opacity: mounted ? 0.4 : 0, transition: 'opacity 1.5s ease 0.5s' }}>
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-[#C4933A] to-transparent" />
        <div className="absolute top-0 left-0 h-full w-px bg-gradient-to-b from-[#C4933A] to-transparent" />
      </div>
      <div className="absolute top-8 right-8 w-16 h-16 pointer-events-none"
        style={{ opacity: mounted ? 0.4 : 0, transition: 'opacity 1.5s ease 0.5s' }}>
        <div className="absolute top-0 right-0 w-full h-px bg-gradient-to-l from-[#C4933A] to-transparent" />
        <div className="absolute top-0 right-0 h-full w-px bg-gradient-to-b from-[#C4933A] to-transparent" />
      </div>
      <div className="absolute bottom-8 left-8 w-16 h-16 pointer-events-none"
        style={{ opacity: mounted ? 0.4 : 0, transition: 'opacity 1.5s ease 0.5s' }}>
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-[#C4933A] to-transparent" />
        <div className="absolute bottom-0 left-0 h-full w-px bg-gradient-to-t from-[#C4933A] to-transparent" />
      </div>
      <div className="absolute bottom-8 right-8 w-16 h-16 pointer-events-none"
        style={{ opacity: mounted ? 0.4 : 0, transition: 'opacity 1.5s ease 0.5s' }}>
        <div className="absolute bottom-0 right-0 w-full h-px bg-gradient-to-l from-[#C4933A] to-transparent" />
        <div className="absolute bottom-0 right-0 h-full w-px bg-gradient-to-t from-[#C4933A] to-transparent" />
      </div>

      {/* KP watermark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
        <span
          className="font-display leading-none"
          style={{
            fontSize: 'clamp(180px, 38vw, 560px)',
            fontWeight: 300,
            color: 'rgba(196, 147, 58, 0.05)',
            lineHeight: 1,
          }}
        >
          KP
        </span>
      </div>

      {/* Main content */}
      <div className="relative flex flex-col items-center px-6 text-center z-10">
        {/* Logo mark */}
        <div
          className="mb-6"
          style={{
            opacity: mounted ? 1 : 0,
            transition: 'opacity 1s ease 0.1s',
          }}
        >
          <p className="font-body text-[9px] tracking-[0.5em] uppercase" style={{ color: 'rgba(196,147,58,0.6)' }}>
            Key Privé
          </p>
          <div className="h-px w-12 mx-auto mt-2" style={{ background: 'linear-gradient(90deg, transparent, #C4933A, transparent)' }} />
        </div>

        <h2
          className="font-display text-5xl md:text-7xl font-light text-[#F2EDE6] tracking-[0.08em] leading-none mb-4"
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 1s ease 0.3s, transform 1s cubic-bezier(0.16, 1, 0.3, 1) 0.3s',
          }}
        >
          Coming Soon
        </h2>

        <p
          className="font-body text-sm font-light max-w-xs leading-relaxed"
          style={{
            color: 'rgba(242,237,230,0.4)',
            opacity: mounted ? 1 : 0,
            transition: 'opacity 1s ease 0.5s',
          }}
        >
          London&apos;s private property office — launching shortly.
        </p>

        {/* Enter site link */}
        {onEnter && (
          <button
            onClick={onEnter}
            className="mt-8 flex items-center gap-2 font-body text-[10px] tracking-[0.3em] uppercase transition-all duration-300 group"
            style={{
              color: 'rgba(242,237,230,0.35)',
              opacity: mounted ? 1 : 0,
              transition: `opacity 1.2s ease 0.9s`,
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#C4933A'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'rgba(242,237,230,0.35)'; }}
          >
            Enter Site
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="transition-transform duration-300 group-hover:translate-x-1"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>

      {/* CTA Bar */}
      <div
        className="absolute bottom-16 left-0 right-0 flex flex-col items-center px-6 z-10"
        style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 1.2s ease 0.8s, transform 1.2s cubic-bezier(0.16, 1, 0.3, 1) 0.8s',
        }}
      >
        <p className="text-[#C4933A] text-xs tracking-[0.25em] uppercase mb-4 font-light">
          Register Your Interest
        </p>

        {submitted ? (
          <div className="flex flex-col items-center gap-2 w-full max-w-md">
            <div
              className="flex items-center gap-3 w-full border border-[#C4933A]/60 px-5 py-3"
              style={{ background: 'rgba(196, 147, 58, 0.08)' }}
            >
              <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="9" stroke="#C4933A" strokeWidth="1.5" />
                <path d="M6 10l3 3 5-5" stroke="#C4933A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <p className="text-[#C4933A] text-xs tracking-[0.15em] uppercase font-light">
                Thank you — we&apos;ll be in touch soon.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-start w-full max-w-md gap-1">
            <div className="flex flex-col sm:flex-row items-stretch gap-3 w-full">
              <div className="flex flex-col flex-1 gap-1">
                <input
                  type="email"
                  placeholder="Your email address"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={() => setEmailError(validateEmail(email))}
                  className="w-full bg-transparent border text-[#F2EDE6] placeholder-[#F2EDE6]/30 text-sm px-5 py-3 outline-none transition-colors duration-300"
                  style={{
                    letterSpacing: '0.05em',
                    borderColor: emailError ? 'rgba(220, 80, 60, 0.7)' : 'rgba(196, 147, 58, 0.4)',
                  }}
                  onFocus={e => {
                    if (!emailError) e.currentTarget.style.borderColor = 'rgba(196, 147, 58, 0.8)';
                  }}
                  onBlurCapture={e => {
                    if (!emailError) e.currentTarget.style.borderColor = 'rgba(196, 147, 58, 0.4)';
                  }}
                />
                {emailError && (
                  <p className="text-[10px] tracking-[0.1em] font-light" style={{ color: 'rgba(220, 100, 80, 0.9)' }}>
                    {emailError}
                  </p>
                )}
                {submitError && (
                  <p className="text-[10px] tracking-[0.1em] font-light" style={{ color: 'rgba(220, 100, 80, 0.9)' }}>
                    {submitError}
                  </p>
                )}
              </div>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full sm:w-auto px-8 py-3 text-xs tracking-[0.2em] uppercase font-light transition-all duration-300 disabled:opacity-60"
                style={{
                  background: 'linear-gradient(135deg, #C4933A, #D4A84B)',
                  color: '#1A0E08',
                  letterSpacing: '0.2em',
                  alignSelf: 'flex-start',
                }}
                onMouseEnter={e => { if (!submitting) e.currentTarget.style.opacity = '0.85'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
              >
                {submitting ? 'Sending…' : 'Notify Me'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bottom gold line */}
      <div
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(196,147,58,0.4), transparent)' }}
      />
    </div>
  );
}
